import React from 'react';
import { Plus, Trash2, Edit2, Save, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';

/**
 * Pestaña de gestión de ensayos y subcategorías
 */
const EnsayosTab = ({
  ensayos,
  newEnsayo,
  onNewEnsayoChange,
  editingEnsayo,
  onEditingEnsayoChange,
  onAddEnsayo,
  onUpdateEnsayo,
  onDeleteEnsayo,
  onCancelEdit,
  // Subcategory handlers
  onAddSubcategory,
  onUpdateSubcategory,
  onRemoveSubcategory,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Gestión de Ensayos y Subcategorías</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add New Ensayo */}
        <div className="border p-4 rounded-lg bg-blue-50">
          <h3 className="font-semibold mb-3">Nuevo Ensayo</h3>
          <div className="space-y-3">
            <Input
              placeholder="Nombre del ensayo"
              value={newEnsayo.name}
              onChange={(e) => onNewEnsayoChange({ ...newEnsayo, name: e.target.value })}
              data-testid="new-ensayo-name"
            />
            <div className="space-y-2">
              <Label>Subcategorías</Label>
              {newEnsayo.subcategories.map((sub, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    placeholder={`Subcategoría ${idx + 1}`}
                    value={sub}
                    onChange={(e) => onUpdateSubcategory(idx, e.target.value, false)}
                    data-testid={`new-subcategory-${idx}`}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRemoveSubcategory(idx, false)}
                    disabled={newEnsayo.subcategories.length === 1}
                  >
                    <X size={16} />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={() => onAddSubcategory(false)} size="sm">
                <Plus size={16} className="mr-1" />
                Añadir Subcategoría
              </Button>
            </div>
            <Button onClick={onAddEnsayo} data-testid="add-ensayo-btn">
              <Save size={18} className="mr-1" />
              Guardar Ensayo
            </Button>
          </div>
        </div>

        {/* Existing Ensayos */}
        <div className="space-y-3">
          <h3 className="font-semibold">Ensayos Existentes</h3>
          {ensayos.map((ensayo) => (
            <div
              key={ensayo.id}
              className="border p-4 rounded-lg"
              data-testid={`ensayo-${ensayo.id}`}
            >
              {editingEnsayo?.id === ensayo.id ? (
                <div className="space-y-3">
                  <Input
                    value={editingEnsayo.name}
                    onChange={(e) =>
                      onEditingEnsayoChange({ ...editingEnsayo, name: e.target.value })
                    }
                  />
                  <div className="space-y-2">
                    <Label>Subcategorías</Label>
                    {editingEnsayo.subcategories.map((sub, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          value={sub}
                          onChange={(e) => onUpdateSubcategory(idx, e.target.value, true)}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onRemoveSubcategory(idx, true)}
                          disabled={editingEnsayo.subcategories.length === 1}
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" onClick={() => onAddSubcategory(true)} size="sm">
                      <Plus size={16} className="mr-1" />
                      Añadir Subcategoría
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={onUpdateEnsayo} size="sm">
                      <Save size={16} className="mr-1" />
                      Guardar
                    </Button>
                    <Button variant="outline" onClick={onCancelEdit} size="sm">
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-lg">{ensayo.name}</h4>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditingEnsayoChange(ensayo)}
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onDeleteEnsayo(ensayo.id, ensayo.name)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {ensayo.subcategories.map((sub, idx) => (
                      <Badge key={idx} variant="secondary">
                        {sub}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default EnsayosTab;
